import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Compass,
  Pencil,
  Plus,
} from "lucide-react";
import {
  getAllActivities,
  getJoiningActivities,
  getCreatedActivities,
} from "@/services/api/activity";
import type { ActivityInfo, ActivityJoinRow } from "@/services/api/activity";
import AdminEmptyState from "@/components/AdminEmptyState";
import StudentActivityCard from "@/components/student/StudentActivityCard";

const PAGE_SIZE = 20;

type PaginationItem = number | "ellipsis-start" | "ellipsis-end";

const getPaginationItems = (
  currentPage: number,
  totalPages: number,
): PaginationItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: PaginationItem[] = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) items.push("ellipsis-start");
  for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
    items.push(pageNumber);
  }
  if (end < totalPages - 1) items.push("ellipsis-end");
  items.push(totalPages);

  return items;
};

type TabKey = "all" | "joining" | "created";

const TABS: { key: TabKey; label: string; icon: typeof Compass }[] = [
  { key: "all", label: "All", icon: Compass },
  { key: "joining", label: "Joining", icon: CheckCircle2 },
  { key: "created", label: "Created", icon: Pencil },
];

const ActivityGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={index}
        className="animate-pulse overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
      >
        <div className="aspect-[16/9] w-full bg-gray-100" />
        <div className="space-y-3 p-4">
          <div className="h-4 w-4/5 rounded-lg bg-gray-100" />
          <div className="h-3 w-2/5 rounded-md bg-gray-100" />
          <div className="h-3 w-3/5 rounded-md bg-gray-100" />
          <div className="h-10 w-full rounded-xl bg-gray-100" />
        </div>
      </div>
    ))}
  </div>
);

const StudentActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [activities, setActivities] = useState<ActivityInfo[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(async (targetPage: number, tab: TabKey) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);

    let resp: any;
    if (tab === "all") {
      resp = await getAllActivities(targetPage);
    } else if (tab === "joining") {
      resp = await getJoiningActivities(targetPage);
    } else {
      resp = await getCreatedActivities(targetPage);
    }

    if (requestId !== requestIdRef.current) return;

    const result = resp?.result;
    if (resp?.status !== 0 || !Array.isArray(result)) {
      setError(true);
      setTotal(0);
      setLoading(false);
      return;
    }

    let items: ActivityInfo[] = [];
    if (tab === "joining") {
      items = result
        .map((row: ActivityJoinRow) => row.activity_info)
        .filter(Boolean) as ActivityInfo[];
    } else {
      items = result as ActivityInfo[];
    }

    setError(false);
    setPage(targetPage);
    setTotal(Number(resp.total) || 0);
    setActivities(items);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPage(1, activeTab);
  }, [fetchPage, activeTab]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const paginationItems = getPaginationItems(page, totalPages);

  const goToPage = (targetPage: number) => {
    if (loading || targetPage === page || targetPage < 1 || targetPage > totalPages) {
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchPage(targetPage, activeTab);
  };

  const emptyState =
    activeTab === "joining"
      ? {
          title: t("No activities yet"),
          message: t("Activities you join will appear here."),
          actionLabel: t("Browse activities"),
          onAction: () => setActiveTab("all"),
        }
      : activeTab === "created"
        ? {
            title: t("No activities yet"),
            message: t("Activities you create will appear here."),
            actionLabel: t("Create Activity"),
            onAction: () => navigate("/student/activities/new"),
          }
        : {
            title: t("No activities yet"),
            message: t("Activities will appear here."),
          };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#fff3e0] text-[#ffa217]">
            <Compass size={22} />
          </span>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">
              {t("Activities")}
            </h1>
            <p className="mt-1 max-w-xl text-sm text-gray-500">
              {t("Discover activities, join in and manage the ones you create.")}
            </p>
          </div>
        </div>
        <Link
          to="/student/activities/new"
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-[#ffa217] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#e08f0e]"
        >
          <Plus size={16} />
          {t("Create Activity")}
        </Link>
      </div>

      <div className="mt-6 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm">
        <div className="flex gap-1.5 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                aria-pressed={isActive}
                className={`inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  isActive
                    ? "bg-[#ffa217] text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-50 hover:text-[#ffa217]"
                }`}
              >
                <Icon size={15} />
                {t(tab.label)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <ActivityGridSkeleton count={6} />
        ) : error ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="cloud-alert-outline"
              title={t("Something went wrong")}
              message={t("Pull to refresh to load activities.")}
              actionLabel={t("Retry")}
              onAction={() => fetchPage(1, activeTab)}
            />
          </div>
        ) : activities.length === 0 ? (
          <div className="rounded-2xl border border-gray-100 bg-white">
            <AdminEmptyState
              iconType="MaterialCommunityIcons"
              iconName="calendar-blank-outline"
              title={emptyState.title}
              message={emptyState.message}
              actionLabel={emptyState.actionLabel}
              onAction={emptyState.onAction}
            />
          </div>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {activities.map((activity) => (
                <StudentActivityCard
                  key={activity.id}
                  activity={activity}
                  showEdit={activeTab === "created"}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <nav
                aria-label="Pagination"
                className="mt-8 flex items-center justify-center gap-1.5"
              >
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || loading}
                  aria-label={t("Previous page")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={17} />
                </button>
                {paginationItems.map((item) =>
                  typeof item === "number" ? (
                    <button
                      key={item}
                      type="button"
                      onClick={() => goToPage(item)}
                      disabled={loading}
                      aria-current={item === page ? "page" : undefined}
                      className={`inline-flex h-9 min-w-9 items-center justify-center rounded-xl border px-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                        item === page
                          ? "border-[#ffa217] bg-[#ffa217] text-white shadow-sm"
                          : "border-gray-200 bg-white text-gray-600 hover:border-[#ffa217] hover:text-[#ffa217]"
                      }`}
                    >
                      {item}
                    </button>
                  ) : (
                    <span
                      key={item}
                      className="inline-flex h-9 w-7 items-center justify-center text-sm text-gray-400"
                    >
                      …
                    </span>
                  ),
                )}
                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || loading}
                  aria-label={t("Next page")}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight size={17} />
                </button>
              </nav>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentActivitiesPage;
